/**
 * This component contains code to generate pin, pin is valid only for 24 hours.
 * if a pin is already existing then this modal will not be accessible.
 */
import {
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  CardHeader,
  Divider,
  DialogActions
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "react-i18next";
import DashedDivider from "common/ui/DashedDivider";
import { useAddServiceRecord } from "services/machines.service";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { toast } from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { setRequestSoftware, setGeneratePin } from "context/modal.context";
import { selectGeneratePinProps } from "context/modal.selector";
import LoadingButton from "common/ui/LoadingButton";

function GeneratePin() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const props = useAppSelector(selectGeneratePinProps);
  const machineId = props.data?.machineSummary.id;

  const serviceRecord = useAddServiceRecord(onServiceRecordFetch);

  async function onServiceRecordFetch(_data, error) {
    await queryClient.invalidateQueries({
      queryKey: ["machine", "detail"],
      exact: false
    });
    if (!error) {
      toast.success(t("generatePin.pinGeneratedSuccessfully"));
      dispatch(setRequestSoftware({ open: true, machineId: machineId }));
    }

    dismiss();
  }

  function handleGeneratePin() {
    serviceRecord.mutate(props.data?.machineSummary.id);
  }

  function dismiss() {
    dispatch(setGeneratePin({ open: false, data: null }));
  }

  return (
    <Dialog open={props.open}>
      <DialogTitle
        component={CardHeader}
        color="primary"
        avatar={<SettingsIcon />}
        title={t("generatePin.machineService")}
        titleTypographyProps={{ variant: "h6" }}
        action={
          <IconButton onClick={dismiss}>
            <CloseIcon />
          </IconButton>
        }
      />
      <DialogContent sx={{ py: 0 }}>
        <DashedDivider />
        <Typography align="center" marginTop={2}>
          {t("generatePin.longText")}
        </Typography>
        <Grid container sx={{ color: "grey", my: 2, px: 3 }}>
          <Grid xs={6} item container justifyContent="space-between">
            <Typography variant="body1" alignSelf="center" noWrap>
              {t("generatePin.machineSerialNumber")}
            </Typography>
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ borderColor: "gray.dark" }}
            />
          </Grid>
          <Grid xs={6} item>
            <Typography variant="subtitle1" fontWeight="bold" ml={5} noWrap>
              {_.defaultTo(props.data?.machineSummary.serialNumber, "")}
            </Typography>
          </Grid>
          <Grid xs={6} item container justifyContent="space-between">
            <Typography variant="body1" alignSelf="center" noWrap>
              {t("generatePin.machineNameOrNumber")}
            </Typography>
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ borderColor: "gray.dark" }}
            />
          </Grid>
          <Grid xs={6} item>
            <Typography variant="subtitle1" fontWeight="bold" ml={5} noWrap>
              {_.defaultTo(props.data?.machineSummary.name, "")}
            </Typography>
          </Grid>
        </Grid>
        <DashedDivider />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", py: 2 }}>
        <LoadingButton
          loading={serviceRecord.isLoading}
          type="submit"
          variant="outlined"
          onClick={handleGeneratePin}
        >
          {t("generatePin.generateServicePin")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default GeneratePin;
